import React, { createContext, useContext, useState } from 'react';

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = createContext<TabsContextValue>({ value: '', onValueChange: () => {} });

interface TabsProps {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const Tabs: React.FC<TabsProps> = ({ value: controlledValue, defaultValue = '', onValueChange, children, className = '', 'data-testid': testId }) => {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const handleChange = (v: string) => {
    if (!isControlled) setUncontrolledValue(v);
    onValueChange?.(v);
  };

  return (
    <TabsContext.Provider value={{ value, onValueChange: handleChange }}>
      <div className={className} data-testid={testId}>{children}</div>
    </TabsContext.Provider>
  );
};

export const TabsList: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div role="tablist" className={`inline-flex h-10 items-center rounded-lg bg-gray-50 p-1 gap-1 ${className}`}>
    {children}
  </div>
);

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({ value, children, className = '', disabled, 'data-testid': testId }) => {
  const { value: activeValue, onValueChange } = useContext(TabsContext);
  const isActive = activeValue === value;

  return (
    <button
      role="tab"
      aria-selected={isActive}
      disabled={disabled}
      data-testid={testId}
      onClick={() => onValueChange(value)}
      className={[
        'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-all',
        'focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed',
        isActive
          ? 'bg-white text-primary shadow-sm'
          : 'text-text-secondary hover:text-text-primary',
        className,
      ].join(' ')}
    >
      {children}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export const TabsContent: React.FC<TabsContentProps> = ({ value, children, className = '' }) => {
  const { value: activeValue } = useContext(TabsContext);
  if (activeValue !== value) return null;

  return (
    <div role="tabpanel" className={`mt-2 ${className}`}>
      {children}
    </div>
  );
};
